import * as React from "react";
import { createPortal } from "react-dom";
import { Search, Check } from "lucide-react";
import { normSearch } from "@/lib/searchText";

const MIN_OPTIONS = 7;
const setNativeValue = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value").set;

const eligible = (el) => el && el.tagName === "SELECT" && !el.multiple && !el.disabled && el.size <= 1
  && !el.hasAttribute("data-nosearch") && el.options.length >= MIN_OPTIONS;

function placement(sel) {
  const host = sel.closest('[role="dialog"],[role="alertdialog"]');
  const r = sel.getBoundingClientRect();
  if (host) {
    return { host, style: { position: "absolute", top: host.scrollTop, left: 0, width: "100%", height: host.clientHeight }, sheet: true };
  }
  const below = window.innerHeight - r.bottom;
  const up = below < 280 && r.top > below;
  const maxH = Math.min(360, (up ? r.top : below) - 12);
  const width = Math.max(r.width, 220);
  const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
  return { host: document.body, style: { position: "fixed", left, width, maxHeight: maxH, ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) }, sheet: false };
}

const Picker = ({ sel, onClose }) => {
  const [q, setQ] = React.useState("");
  const inputRef = React.useRef(null);
  const pos = React.useMemo(() => placement(sel), [sel]);
  const opts = React.useMemo(() => Array.from(sel.options).map((o, i) => ({ i, v: o.value, t: o.text, d: o.disabled, g: o.parentElement?.tagName === "OPTGROUP" ? o.parentElement.label : "" })), [sel]);
  const nq = normSearch(q);
  const list = nq ? opts.filter((o) => normSearch(`${o.t} ${o.g}`).includes(nq)) : opts;
  React.useEffect(() => { const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 30); return () => clearTimeout(t); }, []);
  React.useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [onClose]);
  const pick = (o) => {
    if (o.d) return;
    setNativeValue.call(sel, o.v);
    sel.dispatchEvent(new Event("input", { bubbles: true }));
    sel.dispatchEvent(new Event("change", { bubbles: true }));
    onClose();
  };
  const onKey = (e) => {
    e.stopPropagation();
    if (e.key === "Enter") { e.preventDefault(); const f = list.find((o) => !o.d); if (f) pick(f); }
  };
  const panel = (
    <div dir="rtl" className={`flex flex-col overflow-hidden border bg-white text-slate-800 shadow-xl ${pos.sheet ? "rounded-lg" : "rounded-md"}`}
      style={pos.sheet ? { height: "100%" } : { maxHeight: pos.style.maxHeight }} onMouseDown={(e) => e.stopPropagation()} data-testid="native-select-search-panel">
      <div className="relative border-b p-2">
        <Search className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} dir="auto" placeholder="ابحث..."
          className="h-9 w-full rounded-md border border-slate-200 pr-8 pl-2 text-sm outline-none focus:ring-1 focus:ring-slate-400" data-testid="native-select-search-input" />
      </div>
      <div className="flex-1 overflow-y-auto p-1">
        {list.map((o) => (
          <button type="button" key={o.i} disabled={o.d} onClick={() => pick(o)} data-testid={`native-select-option-${o.i}`}
            className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-right text-sm hover:bg-slate-100 disabled:opacity-40 ${o.v === sel.value ? "bg-slate-100 font-semibold" : ""}`}>
            <span className="truncate">{o.t}</span>{o.v === sel.value && <Check className="h-4 w-4 shrink-0" />}
          </button>
        ))}
        {!list.length && <div className="py-4 text-center text-xs text-slate-400" data-testid="native-select-search-empty">لا توجد نتائج</div>}
      </div>
    </div>
  );
  return createPortal(
    <>
      {!pos.sheet && <div className="fixed inset-0 z-[9998]" onMouseDown={onClose} onTouchStart={onClose} />}
      <div className="z-[9999]" style={{ ...pos.style, pointerEvents: "auto" }}>{panel}</div>
    </>, pos.host);
};

export const NativeSelectSearch = () => {
  const [sel, setSel] = React.useState(null);
  React.useEffect(() => {
    const open = (e) => {
      const el = e.target;
      if (!eligible(el)) return;
      e.preventDefault();
      el.focus({ preventScroll: true });
      setSel(el);
    };
    const key = (e) => {
      if (eligible(e.target) && (e.key === "Enter" || e.key === " " || (e.altKey && e.key === "ArrowDown"))) open(e);
    };
    document.addEventListener("mousedown", open, true);
    document.addEventListener("touchstart", open, { capture: true, passive: false });
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("mousedown", open, true);
      document.removeEventListener("touchstart", open, { capture: true });
      document.removeEventListener("keydown", key, true);
    };
  }, []);
  React.useEffect(() => {
    if (!sel) return undefined;
    const close = () => setSel(null);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, [sel]);
  return sel ? <Picker sel={sel} onClose={() => setSel(null)} /> : null;
};
